<template>
    <div id="integrated-terminal" className="bg-dark p-3"></div>
    <WalletModalProvider ref="walletMod">
        <template #default="modalScope">
            <div @click="modalScope.openModal()" ref="trigger"></div>
        </template>
    </WalletModalProvider>
</template>

<script>
import {ref, computed, watch, onMounted, unref, isRef, nextTick} from 'vue'
import {initWallet, useWallet, WalletModalProvider, WalletMultiButton} from 'solana-wallets-vue'
import {ReferralProvider} from '@jup-ag/referral-sdk'
import {Connection, PublicKey} from "@solana/web3.js";

import { bus } from '../walletBus'

export default {
    components: {WalletModalProvider, WalletMultiButton},
    props: {
        inputToken: {
            type: String,
            default: 'So11111111111111111111111111111111111111112',
        },
        outputToken: {
            type: String,
            default: 'Comp4ssDzXcLeu2MnLuGNNFC4cmLPMng8qWHPvzAMU1h',
        },
        referrerWallet: {
            type: String,
            default: '67BRW1TKFVN4nNx8uyvhdCdKK24wxRxf3rtnJG4jxTBm',
        },
        feeBps: {
            type: Number,
            default: 50,
        },
        fixed: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, {emit}) {
        // set local storage: 'jupiter-terminal-global-priority-fee' to 0.01
        localStorage.setItem('jupiter-terminal-global-priority-fee', 0.01)
        localStorage.setItem('jupiter-terminal-global-priority-level', '"VERY_HIGH"')


        const JUPITER_PROJECT = new PublicKey(
            "45ruCyfdRkWpRNGEqWzjCiXRHkZs8WXCLQ67Pnpye7Hp",
        );
        const connection = new Connection('https://specific-lanny-fast-mainnet.helius-rpc.com/')
        const jupiterInstance = ref(null)
        const wallet = useWallet()

        const getReferralAccount = async () => {
            const provider = new ReferralProvider(connection)
            const referralAccounts = await provider.getReferralAccounts([
                {
                    memcmp: {
                        offset: 8,
                        bytes: props.referrerWallet,
                    },
                },
            ]);
            const referralAccount = referralAccounts.filter((obj) =>
                obj.account.project.toString() === JUPITER_PROJECT.toString()
            )[0];
            return referralAccount;
        }

        const loadJupiterScript = () => {
            return new Promise((resolve, reject) => {
                if (window.Jupiter) {
                    resolve(window.Jupiter);
                    return;
                }
                const script = document.createElement('script');
                script.src = 'https://terminal.jup.ag/main-v3.js';
                script.async = true;
                script.onload = () => resolve(window.Jupiter);
                script.onerror = () => reject(new Error('Failed to load Jupiter script'));
                // document.body.appendChild(script);
            });
        };

        // Convert wallet state to match WalletContextState interface
        const getWalletContextState = () => {
            if (!wallet) return null;

            return {
                autoConnect: wallet.autoConnect.value,
                wallets: wallet.wallets.value,
                wallet: wallet.wallet.value,
                publicKey: wallet.publicKey.value,
                connected: wallet.connected.value,
                connecting: wallet.connecting.value,
                disconnecting: wallet.disconnecting.value,
                select: wallet.select.value,
                connect: wallet.connect.value,
                disconnect: wallet.disconnect.value,
                signTransaction: wallet.signTransaction.value,
                signAllTransactions: wallet.signAllTransactions.value,
                signMessage: wallet.signMessage.value,
            };
        }

        // Sync wallet state with Jupiter
        const syncWalletState = () => {
            if (!window.Jupiter?.syncProps) {
                // console.log('Jupiter sync not available');
                return;
            }

            const walletState = getWalletContextState();
            if (!walletState) {
                // console.log('No wallet state to sync');
                return;
            }

            // console.log('Syncing wallet state:', walletState);
            window.Jupiter.syncProps({
                passthroughWalletContextState: walletState
            });
        }

        // Watch for wallet connection changes
        watch(() => wallet.connected.value, (newConnected, oldConnected) => {
            // console.log('Wallet connection changed:', {old: oldConnected, new: newConnected});
            syncWalletState();
        });
        const walletModal = ref(null); // Create a ref for the modal
        const trigger = ref(null); // Create a ref for the trigger element
        const handleWalletConnect = async () => {
            console.log('Jupiter requesting wallet connection');
            if (!wallet.connected.value) {
                try {
                    // If wallet is selected but not connected, try connecting
                    if (wallet.wallet.value) {
                        await wallet.connect();
                    } else {
                        // If no wallet is selected, might want to trigger your wallet selection UI
                        // This depends on how your app handles wallet selection
                        // bus.value.set('open-wallet-modal', true);
                        trigger.value.click()
                    }
                } catch (error) {
                    console.error('Failed to connect wallet:', error);
                }
            } else {
                await wallet.disconnect();
            }
        }
        // Initialize Jupiter
        const initJupiter = async () => {
            try {
                const jupiter = await loadJupiterScript();
                const walletState = getWalletContextState();
                const referralAccount = await getReferralAccount();

                const jupProps = {
                    enableWalletPassthrough: true,
                    passthroughWalletContextState: walletState,
                    endpoint: 'https://specific-lanny-fast-mainnet.helius-rpc.com',
                    displayMode: "integrated",
                    integratedTargetId: "integrated-terminal",
                    onRequestConnectWallet: handleWalletConnect,
                    formProps: {
                        fixedOutputMint: props.fixed,
                        swapMode: "ExactIn",
                        initialInputMint: props.inputToken,
                        initialOutputMint: props.outputToken,
                    },
                    onSuccess(txid, swapResult, quoteResponseMeta) {
                        console.log("Swap successful!", {txid, swapResult, quoteResponseMeta});
                        // if window.clikcy is available, you can use it to track the swap
                        if (window.clicky) {
                            window.clicky.goal('JupiterSwap')
                        }
                    }
                };

                if (referralAccount) {
                    jupProps.platformFeeAndAccounts = {
                        referralAccount: referralAccount.publicKey,
                        feeBps: props.feeBps
                    };
                }

                console.log('Initializing Jupiter with:', jupProps);
                jupiter.init(jupProps);
                jupiterInstance.value = jupiter;
            } catch (error) {
                console.error("Failed to initialize Jupiter:", error);
            }
        }

        onMounted(async () => {
            await nextTick()
            setTimeout(() => {
                console.log('Wallet modal ref:', walletModal.value)
                initJupiter();
            },200)
        })

        return {
            wallet, walletModal, trigger
        }
    }
}
</script>

<style>
#integrated-terminal {
    border-radius: 5px;
    background: #4a6076;
}
#jupiter-terminal {
    min-height:450px;
}

#jupiter-terminal .py-5 {
    padding-bottom: 1.25rem !important;
    padding-top: 1.25rem !important;
}
#jupiter-terminal .px-4 {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
}

#jupiter-terminal .flex-col {
    flex-direction: column !important;
}
#jupiter-terminal .flex {
    display: flex !important;
}
#jupiter-terminal .py-5 {
    padding-bottom: 1.25rem !important;
    padding-top: 1.25rem !important;
}
#jupiter-terminal .px-4 {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
}
#jupiter-terminal .h-8,
#jupiter-terminal .w-8 {
    height: 1.5rem !important;
    width: 1.5rem !important;
}


</style>
